import type {
  AttendanceRecord,
  CalendarEvent,
  Classroom,
  DailyRecord,
  HomeroomHomeworkRecord,
  HomeroomRecord,
  HomeworkAssignment,
  HomeworkRecord,
  QuizDocument,
  QuizResponse,
  QuizSession,
  SeatingChart,
  Student,
  StudentRoutineItem,
  Workspace,
} from "@classroom/domain";
import type { User } from "@supabase/supabase-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { syncReviewId } from "./driveSync/reviewScope";
import { DATA_PROCESSING_VERSION, PRIVACY_VERSION, TERMS_VERSION } from "./legal";
import type { HistoryPlan } from "./recordImport";
import { backend, supabase, supabaseConfigured } from "./supabase";

export type WorkspaceData = {
  workspace?: Workspace;
  hiddenTools: string[];
  classes: Classroom[];
  students: Student[];
  dailyRecords: DailyRecord[];
  homeworkAssignments: HomeworkAssignment[];
  homeworkRecords: HomeworkRecord[];
  calendarEvents: CalendarEvent[];
  homeroomRecords: HomeroomRecord[];
  attendanceRecords: AttendanceRecord[];
  homeroomHomeworkRecords: HomeroomHomeworkRecord[];
  studentRoutineItems: StudentRoutineItem[];
  seatingCharts: SeatingChart[];
  quizzes: QuizDocument[];
  quizSessions: QuizSession[];
  quizResponses: QuizResponse[];
  loading: boolean;
  error?: string;
};
type Collection = Exclude<keyof WorkspaceData, "workspace" | "hiddenTools" | "loading" | "error">;
const collections: Collection[] = [
  "classes",
  "students",
  "dailyRecords",
  "homeworkAssignments",
  "homeworkRecords",
  "calendarEvents",
  "homeroomRecords",
  "attendanceRecords",
  "homeroomHomeworkRecords",
  "studentRoutineItems",
  "seatingCharts",
  "quizzes",
  "quizSessions",
  "quizResponses",
];
const isCollection = (name: string): name is Collection => collections.includes(name as Collection);
const emptyData = (): WorkspaceData => ({
  hiddenTools: [],
  classes: [],
  students: [],
  dailyRecords: [],
  homeworkAssignments: [],
  homeworkRecords: [],
  calendarEvents: [],
  homeroomRecords: [],
  attendanceRecords: [],
  homeroomHomeworkRecords: [],
  studentRoutineItems: [],
  seatingCharts: [],
  quizzes: [],
  quizSessions: [],
  quizResponses: [],
  loading: true,
});

type RecordRow = { collection: Collection; record_id: string; data: { id: string } };
function check(error: { message: string } | null) {
  if (error) throw new Error(error.message);
}

export function useAuthSession() {
  const [user, setUser] = useState<User | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const logoutChannel = useRef<BroadcastChannel | null>(null);
  const [loading, setLoading] = useState(Boolean(supabase));
  useEffect(() => {
    if (!supabase) return;
    let mounted = true;
    const channel = new BroadcastChannel("classroom-explicit-signout");
    logoutChannel.current = channel;
    channel.onmessage = (event) => {
      if (event.data === "signed-out" && mounted) {
        setUser(null);
        setSignedOut(true);
      }
    };
    // Cached identity can open read-only local recovery; Beta write access is
    // separately verified by the server. Cloud requests still validate Auth/RLS.
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) {
        setUser(data.session?.user ?? null);
        setLoading(false);
      }
    });
    const identityChanged = (event: StorageEvent) => {
      const projectUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!projectUrl) return;
      const authKey = `sb-${new URL(projectUrl).hostname.split(".")[0]}-auth-token`;
      if (event.key !== authKey || !event.newValue) return;
      try {
        const next = JSON.parse(event.newValue)?.user;
        if (mounted && typeof next?.id === "string") {
          setUser(next as User);
          setLoading(false);
        }
      } catch {
        /* Invalid storage cannot select another workspace. */
      }
    };
    window.addEventListener("storage", identityChanged);
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        // Token expiry and failed SDK logout can also emit SIGNED_OUT. Only
        // successful explicit logout closes the local recovery workspace.
        if (session) setSignedOut(false);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener("storage", identityChanged);
      logoutChannel.current = null;
      channel.close();
    };
  }, []);
  return {
    user,
    signedOut,
    loading,
    configured: Boolean(supabase) && supabaseConfigured,
    signInWithGoogle: async () => {
      if (!supabase) throw new Error("尚未設定 Supabase。");
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${location.origin}${syncReviewId() ? `${location.pathname}${location.search}` : "/app"}`,
          queryParams: { prompt: "select_account" },
        },
      });
      check(error);
    },
    signOut: async () => {
      if (supabase) check((await supabase.auth.signOut({ scope: "local" })).error);
      setSignedOut(true);
      logoutChannel.current?.postMessage("signed-out");
    },
  };
}

async function fetchRecords(workspaceId: string): Promise<RecordRow[]> {
  if (!supabase) return [];
  const rows: RecordRow[] = [];
  // PostgREST limits response size; paginate so long-running school histories remain complete.
  for (let offset = 0; ; offset += 500) {
    const { data, error } = await supabase
      .from("workspace_records")
      .select("collection,record_id,data")
      .eq("workspace_id", workspaceId)
      .order("collection")
      .order("record_id")
      .range(offset, offset + 499);
    check(error);
    rows.push(...((data || []) as RecordRow[]));
    if (!data || data.length < 500) break;
  }
  return rows;
}

export function useWorkspaceData(user: User | null): WorkspaceData & { workspaceId?: string } {
  const [workspaceId, setWorkspaceId] = useState<string>();
  const [data, setData] = useState<WorkspaceData>(emptyData);
  useEffect(() => {
    if (!user || !supabase) {
      setWorkspaceId(undefined);
      setData(emptyData());
      return;
    }
    let active = true;
    supabase.rpc("ensure_workspace").then(({ data: id, error }) => {
      if (!active) return;
      if (error) setData((current) => ({ ...current, loading: false, error: error.message }));
      else setWorkspaceId(String(id));
    });
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!workspaceId || !supabase || !user) return;
    const client = supabase;
    let active = true;
    Promise.all([
      client.from("workspaces").select("id,data").eq("id", workspaceId).single(),
      client.from("user_profiles").select("hidden_tools").eq("uid", user.id).single(),
      fetchRecords(workspaceId),
    ])
      .then(([workspace, profile, rows]) => {
        check(workspace.error);
        check(profile.error);
        if (!active) return;
        const grouped = emptyData();
        for (const row of rows)
          if (isCollection(row.collection)) {
            (grouped[row.collection] as Array<{ id: string }>).push(row.data);
          }
        setData({
          ...grouped,
          workspace: { ...workspace.data!.data, id: workspace.data!.id } as Workspace,
          hiddenTools: profile.data?.hidden_tools || [],
          loading: false,
        });
      })
      .catch((error: Error) => {
        if (active) setData((current) => ({ ...current, loading: false, error: error.message }));
      });

    const channel = client
      .channel(`workspace:${workspaceId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "workspace_records", filter: `workspace_id=eq.${workspaceId}` },
        (change) => {
          const next = change.new as RecordRow;
          const old = change.old as RecordRow;
          const kind = change.eventType === "DELETE" ? old.collection : next.collection;
          if (!isCollection(kind)) return;
          setData((current) => {
            const items = current[kind] as Array<{ id: string }>;
            const newItems =
              change.eventType === "DELETE"
                ? items.filter((item) => item.id !== old.record_id)
                : [...items.filter((item) => item.id !== next.record_id), next.data];
            return { ...current, [kind]: newItems };
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "workspaces", filter: `id=eq.${workspaceId}` },
        (change) => {
          const row = change.new as { id: string; data: Workspace };
          setData((current) => ({ ...current, workspace: { ...row.data, id: row.id } }));
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "user_profiles", filter: `uid=eq.${user.id}` },
        (change) => {
          const row = change.new as { hidden_tools: string[] };
          setData((current) => ({ ...current, hiddenTools: row.hidden_tools || [] }));
        },
      )
      .subscribe();
    return () => {
      active = false;
      void client.removeChannel(channel);
    };
  }, [workspaceId, user?.id]);
  return { ...data, workspaceId };
}

function requireCloud(workspaceId?: string): asserts workspaceId is string {
  if (!workspaceId || !supabase) throw new Error("雲端服務尚未連線；預覽模式不會寫入正式資料。");
}

export function useWorkspaceActions(workspaceId?: string) {
  return useMemo(() => {
    async function save(collection: Collection, id: string, value: object) {
      requireCloud(workspaceId);
      const record = { ...value, id, workspaceId };
      check(
        (
          await supabase!
            .from("workspace_records")
            .upsert(
              { workspace_id: workspaceId, collection, record_id: id, data: record },
              { onConflict: "workspace_id,collection,record_id" },
            )
        ).error,
      );
    }
    async function remove(collection: Collection, id: string) {
      requireCloud(workspaceId);
      check(
        (
          await supabase!
            .from("workspace_records")
            .delete()
            .eq("workspace_id", workspaceId)
            .eq("collection", collection)
            .eq("record_id", id)
        ).error,
      );
    }
    async function saveMany(collection: Collection, values: Array<{ id: string }>) {
      requireCloud(workspaceId);
      const rows = values.map((value) => ({
        workspace_id: workspaceId,
        collection,
        record_id: value.id,
        data: { ...value, workspaceId },
      }));
      for (let index = 0; index < rows.length; index += 100)
        check(
          (
            await supabase!
              .from("workspace_records")
              .upsert(rows.slice(index, index + 100), { onConflict: "workspace_id,collection,record_id" })
          ).error,
        );
    }
    async function removeMany(collection: Collection, ids: string[]) {
      requireCloud(workspaceId);
      for (let index = 0; index < ids.length; index += 100)
        check(
          (
            await supabase!
              .from("workspace_records")
              .delete()
              .eq("workspace_id", workspaceId)
              .eq("collection", collection)
              .in("record_id", ids.slice(index, index + 100))
          ).error,
        );
    }
    return {
      async addClass(name: string, classRole: Classroom["classRole"], sharedDataClassId?: string) {
        requireCloud(workspaceId);
        const now = new Date().toISOString();
        await save("classes", crypto.randomUUID(), {
          name,
          classRole,
          sharedDataClassId,
          active: true,
          createdAt: now,
          updatedAt: now,
        });
      },
      async importStudents(classId: string, rows: Array<{ number: number; name: string; gender: string }>) {
        requireCloud(workspaceId);
        const existing = await loadStudentsForClass(workspaceId, classId);
        const now = new Date().toISOString();
        const items = rows.map((row) => {
          const previous = existing.find((student) => student.number === row.number);
          const id = previous?.id || crypto.randomUUID();
          return {
            workspace_id: workspaceId,
            collection: "students",
            record_id: id,
            data: {
              ...previous,
              ...row,
              id,
              workspaceId,
              classId,
              active: true,
              createdAt: previous?.createdAt || now,
              updatedAt: now,
            },
          };
        });
        for (let i = 0; i < items.length; i += 100)
          check(
            (
              await supabase!
                .from("workspace_records")
                .upsert(items.slice(i, i + 100), { onConflict: "workspace_id,collection,record_id" })
            ).error,
          );
      },
      async updateStudent(studentId: string, patch: Partial<Student>) {
        await patchRecord("students", studentId, patch);
      },
      deleteStudentData: (studentId: string) =>
        backend<{ ok: boolean; recordsChanged: number }>("deleteStudentData", { workspaceId, studentId }),
      async getLegalAcceptance() {
        requireCloud(workspaceId);
        const { data: auth } = await supabase!.auth.getUser();
        if (!auth.user) throw new Error("請先登入。");
        const { data, error } = await supabase!
          .from("legal_acceptances")
          .select("accepted_at")
          .eq("workspace_id", workspaceId)
          .eq("uid", auth.user.id)
          .eq("terms_version", TERMS_VERSION)
          .eq("privacy_version", PRIVACY_VERSION)
          .eq("data_processing_version", DATA_PROCESSING_VERSION)
          .maybeSingle();
        check(error);
        return Boolean(data);
      },
      async acceptLegalTerms(profile: {
        organizationName: string;
        contactNameOrTitle: string;
        contactEmail: string;
        contactAddress: string;
      }) {
        requireCloud(workspaceId);
        const { error } = await supabase!.rpc("accept_legal_terms", {
          target_workspace: workspaceId,
          target_terms: TERMS_VERSION,
          target_privacy: PRIVACY_VERSION,
          target_data_processing: DATA_PROCESSING_VERSION,
          authority_confirmed: true,
          target_organization_name: profile.organizationName,
          target_contact_name_or_title: profile.contactNameOrTitle,
          target_contact_email: profile.contactEmail,
          target_contact_address: profile.contactAddress,
        });
        check(error);
      },
      async updateClassSettings(
        classId: string,
        patch: Partial<
          Pick<
            Classroom,
            "customSubjects" | "customDuties" | "dutyAssignments" | "drawState" | "assignmentFolders" | "dailyMemos"
          >
        >,
      ) {
        await patchRecord("classes", classId, patch);
      },
      async saveHiddenTools(uid: string, hiddenTools: string[]) {
        requireCloud(workspaceId);
        const { data: auth } = await supabase!.auth.getUser();
        if (auth.user?.id !== uid) throw new Error("只能修改自己的工具設定。");
        check((await supabase!.from("user_profiles").update({ hidden_tools: hiddenTools }).eq("uid", uid)).error);
      },
      saveDailyRecord: (item: DailyRecord) => save("dailyRecords", item.id, item),
      saveHomeworkAssignment: (item: HomeworkAssignment) => save("homeworkAssignments", item.id, item),
      saveHomeworkRecord: (item: HomeworkRecord) => save("homeworkRecords", item.id, item),
      async saveTeachingRecords(
        events: CalendarEvent[],
        assignments: HomeworkAssignment[],
        records: HomeworkRecord[] = [],
      ) {
        requireCloud(workspaceId);
        const rows = [
          ...events.map((value) => ({ collection: "calendarEvents", value })),
          ...assignments.map((value) => ({ collection: "homeworkAssignments", value })),
          ...records.map((value) => ({ collection: "homeworkRecords", value })),
        ].map(({ collection, value }) => ({
          workspace_id: workspaceId,
          collection,
          record_id: value.id,
          data: { ...value, workspaceId },
        }));
        if (rows.length)
          check(
            (
              await supabase!
                .from("workspace_records")
                .upsert(rows, { onConflict: "workspace_id,collection,record_id" })
            ).error,
          );
      },
      saveCalendarEvent: (item: CalendarEvent) => save("calendarEvents", item.id, item),
      saveCalendarEvents: (items: CalendarEvent[]) => saveMany("calendarEvents", items),
      deleteCalendarEvent: (id: string) => remove("calendarEvents", id),
      deleteCalendarEvents: (ids: string[]) => removeMany("calendarEvents", ids),
      saveHomeroomRecord: (item: HomeroomRecord) => save("homeroomRecords", item.id, item),
      saveAttendanceRecord: (item: AttendanceRecord) => save("attendanceRecords", item.id, item),
      saveHomeroomHomeworkRecord: (item: HomeroomHomeworkRecord) => save("homeroomHomeworkRecords", item.id, item),
      deleteHomeroomHomeworkRecord: (id: string) => remove("homeroomHomeworkRecords", id),
      saveStudentRoutineItem: (item: StudentRoutineItem) => save("studentRoutineItems", item.id, item),
      deleteStudentRoutineItem: (id: string) => remove("studentRoutineItems", id),
      async saveHistoryPlan(plan: HistoryPlan) {
        await saveMany("attendanceRecords", plan.attendanceRecords);
        await saveMany("homeroomHomeworkRecords", plan.homeroomHomeworkRecords);
        await saveMany("dailyRecords", plan.dailyRecords);
        await saveMany("studentRoutineItems", plan.studentRoutineItems);
      },
      saveSeatingChart: (item: SeatingChart) => save("seatingCharts", item.id, item),
      saveQuiz: (item: QuizDocument) => save("quizzes", item.id, item),
      deleteQuiz: (id: string) => remove("quizzes", id),
      createQuizSession: (quizId: string, classId: string, durationMinutes: number) =>
        backend<QuizSession & { joinUrl: string }>("createQuizSession", {
          workspaceId,
          quizId,
          classId,
          durationMinutes,
        }),
      closeQuizSession: (sessionId: string) => backend("closeQuizSession", { workspaceId, sessionId }),
      async checkout() {
        const result = await backend<{ url: string }>("createCheckoutSession", { workspaceId });
        window.location.assign(result.url);
      },
      async billingPortal() {
        const result = await backend<{ url: string }>("createBillingPortal", { workspaceId });
        window.location.assign(result.url);
      },
      exportWorkspace: () => backend("exportWorkspace", { workspaceId }),
      requestDeletion: () => backend<{ graceEndsAt: string }>("requestWorkspaceDeletion", { workspaceId }),
      generateQuiz: (sourceText: string, count: number) =>
        backend<{ title: string; questions: unknown[] }>("generateQuiz", { workspaceId, sourceText, count }),
    };
    async function patchRecord(collection: Collection, id: string, patch: object) {
      requireCloud(workspaceId);
      const { data, error } = await supabase!
        .from("workspace_records")
        .select("data")
        .eq("workspace_id", workspaceId)
        .eq("collection", collection)
        .eq("record_id", id)
        .single();
      check(error);
      await save(collection, id, { ...data!.data, ...patch, updatedAt: new Date().toISOString() });
    }
  }, [workspaceId]);
}

export async function loadStudentsForClass(workspaceId: string, classId: string): Promise<Student[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("workspace_records")
    .select("data")
    .eq("workspace_id", workspaceId)
    .eq("collection", "students")
    .eq("data->>classId", classId);
  check(error);
  return (data || []).map((row) => row.data as Student).sort((a, b) => a.number - b.number);
}
